import { useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { useTenantAlerts, useUpdateAlert } from "@/api/alerts";
import { usePagination } from "@/hooks/use-pagination";
import { useTenantContext } from "@/hooks/use-tenant-context";
import { DataTable } from "@/components/data-table/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { useAuth } from "@/stores/auth-store";
import { canEdit } from "@/lib/roles";
import type { Alert } from "@/api/types";

const severityVariant: Record<string, "danger" | "warning" | "info"> = {
  critical: "danger",
  warning: "warning",
  info: "info",
};

const statusVariant: Record<string, "danger" | "warning" | "success"> = {
  open: "danger",
  acknowledged: "warning",
  resolved: "success",
};

export function TenantAlertsPage() {
  const { user } = useAuth();
  const { tenantId, tenant } = useTenantContext();
  const { page, pageSize, search, params, setPage, setPageSize, setSearch, setFilter } = usePagination();
  const { data, isLoading } = useTenantAlerts(tenantId, params);
  const updateAlert = useUpdateAlert(tenantId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const columns: ColumnDef<Alert, unknown>[] = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => {
        const alert = row.original;
        const isExpanded = expandedId === alert.id;
        return (
          <div>
            <button
              className="flex items-center gap-1 text-left font-medium hover:text-accent"
              onClick={() => setExpandedId(isExpanded ? null : alert.id)}
            >
              {isExpanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
              {alert.title}
            </button>
            {isExpanded && (
              <div className="mt-2 space-y-2 pl-5 text-sm">
                {alert.description && (
                  <p className="text-text-secondary">{alert.description}</p>
                )}
                {alert.device && alert.device_hostname && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-muted">Device:</span>
                    <Link
                      to={`/tenants/${tenantId}/devices/${alert.device}`}
                      className="inline-flex items-center gap-1 rounded bg-accent/10 px-2 py-0.5 text-accent hover:bg-accent/20 hover:underline"
                    >
                      {alert.device_hostname}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                )}
                {alert.context && Object.keys(alert.context).length > 0 && (
                  <dl className="space-y-1">
                    {Object.entries(alert.context).map(([key, val]) => (
                      <div key={key} className="flex gap-2">
                        <dt className="font-medium text-text-muted">{key}:</dt>
                        <dd className="text-text-secondary">{String(val)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "severity",
      header: "Severity",
      cell: ({ row }) => <Badge variant={severityVariant[row.original.severity]}>{row.original.severity}</Badge>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <Badge variant={statusVariant[row.original.status]}>{row.original.status}</Badge>,
    },
    { accessorKey: "alert_type", header: "Type" },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const alert = row.original;
        if (alert.status === "resolved" || !canEdit(user)) return null;
        return (
          <div className="flex gap-1">
            {alert.status === "open" && (
              <Button size="sm" variant="ghost" onClick={() => updateAlert.mutate({ alertId: alert.id, status: "acknowledged" })}>
                Ack
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => updateAlert.mutate({ alertId: alert.id, status: "resolved" })}>
              Resolve
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{tenant?.name ? `${tenant.name} — Alerts` : "Alerts"}</h1>
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search alerts..."
        page={page}
        pageSize={pageSize}
        totalPages={data?.meta?.totalPages ?? 1}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        total={data?.meta?.total}
        toolbar={
          <div className="flex gap-2">
            <Select onChange={(e) => setFilter("severity", e.target.value || null)} className="w-32">
              <option value="">All Severity</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </Select>
            <Select onChange={(e) => setFilter("status", e.target.value || null)} className="w-32">
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
            </Select>
            <Select onChange={(e) => setFilter("alert_type", e.target.value || null)} className="w-44">
              <option value="">All Types</option>
              <option value="device_non_compliant">Device Non-Compliant</option>
              <option value="device_not_seen">Device Not Seen</option>
              <option value="sync_failure">Sync Failure</option>
              <option value="sensor_containment">Sensor Containment</option>
            </Select>
          </div>
        }
      />
    </div>
  );
}
